import {
  LAB_AUDIO_OPTIONS, LAB_EMPHASIS_OPTIONS, LAB_MAX_SCRIPT_LENGTH, LAB_PACE_OPTIONS,
  LAB_PAUSE_OPTIONS, LAB_PITCH_OPTIONS, LAB_TONE_OPTIONS, LAB_TOOL_OPTIONS,
  LAB_TREATMENT_OPTIONS, LAB_VISUAL_CATEGORIES, validateChunks,
  LAB_PURPOSE_OPTIONS, LAB_FOCUS_OPTIONS, LAB_VISUAL_EMPHASIS_OPTIONS, LAB_COMPLEXITY_OPTIONS, LAB_EVIDENCE_OPTIONS,
  LAB_ASSET_TASK_OPTIONS,
  type LabChunk, type LabDeliverySuggestion, type LabDetail, type LabSuggestion, type LabReviewSuggestion, type LabIntentSuggestion,
} from "@kola/shared";

export interface LabDraft {
  version: 1;
  id: string;
  title: string;
  script: string;
  detail: LabDetail;
  productionNotes: string;
  chunks: LabChunk[];
  intents: LabIntentSuggestion[];
  suggestions: LabSuggestion[];
  deliveries: LabDeliverySuggestion[];
  reviews: LabReviewSuggestion[];
  reviewBoundaries: number[];
  segmentModel: string;
  intentModel: string;
  visualModel: string;
  deliveryModel: string;
  reviewModel: string;
  updatedAt: string;
}

export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const draftStorageKey = (userId: string): string =>
  `kola.storyboardLab.v1.${encodeURIComponent(userId)}`;

export function newLabDraft(): LabDraft {
  return {
    version: 1, id: crypto.randomUUID(), title: "Untitled experiment", script: "",
    detail: "fine", productionNotes: "After Effects for 2D motion graphics; Blender for 3D. News clips and still-image sequences can play over an original sound bite.",
    chunks: [], intents: [], suggestions: [], deliveries: [], reviews: [], reviewBoundaries: [],
    segmentModel: "", intentModel: "", visualModel: "", deliveryModel: "", reviewModel: "", updatedAt: new Date().toISOString(),
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isChoice = (value: unknown, options: readonly { id: string }[]): value is string =>
  typeof value === "string" && options.some((option) => option.id === value);

function validResult(value: Record<string, unknown>, chunks: LabChunk[]): boolean {
  return typeof value.chunkId === "string" && chunks.some((chunk) => chunk.id === value.chunkId)
    && (value.confidence === null || (typeof value.confidence === "number" && Number.isFinite(value.confidence)
      && value.confidence >= 0 && value.confidence <= 1)) && typeof value.needsReview === "boolean";
}

export function isLabDraft(value: unknown): value is LabDraft {
  if (!isRecord(value) || value.version !== 1 || typeof value.id !== "string" || !value.id
    || typeof value.title !== "string" || typeof value.script !== "string"
    || value.script.length > LAB_MAX_SCRIPT_LENGTH
    || (value.detail !== "balanced" && value.detail !== "fine")
    || typeof value.productionNotes !== "string" || typeof value.updatedAt !== "string"
    || !Number.isFinite(Date.parse(value.updatedAt))
    || typeof value.segmentModel !== "string" || typeof value.intentModel !== "string" || typeof value.visualModel !== "string"
    || typeof value.deliveryModel !== "string" || typeof value.reviewModel !== "string" || !Array.isArray(value.chunks)
    || !Array.isArray(value.suggestions) || !Array.isArray(value.deliveries)
    || !Array.isArray(value.intents) || !Array.isArray(value.reviews) || !Array.isArray(value.reviewBoundaries)) return false;
  // An unsegmented draft is valid; any populated chunk list must reconstruct
  // every original character, including whitespace, in its original order.
  if (value.chunks.length > 0 && !validateChunks(value.script, value.chunks)) return false;
  const chunks = value.chunks as LabChunk[];
  const script = value.script;
  if (!value.reviewBoundaries.every((offset) => Number.isInteger(offset) && offset > 0 && offset < script.length)) return false;
  if (!value.suggestions.every((entry) => isRecord(entry) && validResult(entry, chunks)
    && isChoice(entry.primary, LAB_VISUAL_CATEGORIES)
    && Array.isArray(entry.supporting) && entry.supporting.length <= 5
    && new Set(entry.supporting).size === entry.supporting.length
    && entry.supporting.every((id) => isChoice(id, LAB_VISUAL_CATEGORIES))
    && Array.isArray(entry.assetTasks) && new Set(entry.assetTasks).size === entry.assetTasks.length
    && entry.assetTasks.every((id) => isChoice(id, LAB_ASSET_TASK_OPTIONS))
    && isChoice(entry.audio, LAB_AUDIO_OPTIONS) && isChoice(entry.treatment, LAB_TREATMENT_OPTIONS)
    && isChoice(entry.tool, LAB_TOOL_OPTIONS) && (entry.continuity === "new" || entry.continuity === "continue"))) return false;
  if (!value.deliveries.every((entry) => isRecord(entry) && validResult(entry, chunks)
    && isChoice(entry.tone, LAB_TONE_OPTIONS) && isChoice(entry.emphasis, LAB_EMPHASIS_OPTIONS)
    && isChoice(entry.pace, LAB_PACE_OPTIONS) && isChoice(entry.pause, LAB_PAUSE_OPTIONS)
    && isChoice(entry.pitch, LAB_PITCH_OPTIONS))) return false;
  if (!value.intents.every((entry) => isRecord(entry) && validResult(entry, chunks)
    && isChoice(entry.purpose, LAB_PURPOSE_OPTIONS) && isChoice(entry.focus, LAB_FOCUS_OPTIONS)
    && isChoice(entry.emphasis, LAB_VISUAL_EMPHASIS_OPTIONS) && isChoice(entry.complexity, LAB_COMPLEXITY_OPTIONS)
    && isChoice(entry.evidence, LAB_EVIDENCE_OPTIONS))) return false;
  if (!value.reviews.every((entry) => isRecord(entry) && validResult(entry, chunks)
    && typeof entry.visualFit === "number" && Number.isFinite(entry.visualFit) && entry.visualFit >= 1 && entry.visualFit <= 5
    && typeof entry.instructionClarity === "number" && Number.isFinite(entry.instructionClarity) && entry.instructionClarity >= 1 && entry.instructionClarity <= 5)) return false;
  return new Set(value.suggestions.map((entry) => entry.chunkId)).size === value.suggestions.length
    && new Set(value.deliveries.map((entry) => entry.chunkId)).size === value.deliveries.length
    && new Set(value.reviews.map((entry) => entry.chunkId)).size === value.reviews.length
    && new Set(value.intents.map((entry) => entry.chunkId)).size === value.intents.length;
}

export function loadLabDrafts(storage: DraftStorage, userId: string): LabDraft[] {
  const raw = storage.getItem(draftStorageKey(userId));
  if (raw === null) return [];
  let value: unknown;
  try { value = JSON.parse(raw); } catch { throw new Error("Saved Lab drafts could not be read. They have been kept on this device; export your current draft before continuing."); }
  if (!Array.isArray(value) || !value.every(isLabDraft)
    || new Set(value.map((draft) => draft.id)).size !== value.length) {
    throw new Error("Saved Lab drafts use an unsupported or damaged format. They have not been overwritten.");
  }
  return value.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}

export function saveLabDraft(storage: DraftStorage, userId: string, draft: LabDraft): LabDraft[] {
  if (!isLabDraft(draft)) throw new Error("This draft contains invalid chunk data and cannot be saved.");
  // Read before write, both to preserve other drafts and to avoid replacing
  // an unreadable archive with a fresh, empty one.
  const drafts = loadLabDrafts(storage, userId);
  const next = [draft, ...drafts.filter((entry) => entry.id !== draft.id)];
  storage.setItem(draftStorageKey(userId), JSON.stringify(next));
  return next;
}

/** Changed text/boundaries invalidates both independent analyses. */
export function withChunks(draft: LabDraft, chunks: LabChunk[], reviewBoundaries: number[] = []): LabDraft {
  if (chunks.length > 0 && !validateChunks(draft.script, chunks)) throw new Error("Chunks must preserve the complete original script.");
  return { ...draft, chunks, reviewBoundaries, intents: [], suggestions: [], deliveries: [], reviews: [], intentModel: "", visualModel: "", deliveryModel: "", reviewModel: "" };
}

/** An asynchronous reply is only accepted for the exact experiment inputs. */
export function labInputSnapshot(draft: LabDraft): string {
  return JSON.stringify([draft.id, draft.script, draft.detail, draft.productionNotes, draft.chunks]);
}

const decisions = (value: { chunkId: string; confidence: number | null; needsReview: boolean } | undefined): string => {
  if (!value) return "";
  const { confidence: _confidence, needsReview: _review, ...content } = value;
  return JSON.stringify(content);
};

/** Following edit plans use the previous visual as continuity context. Keep
 * earlier rows and all host/intent decisions; clear affected visual choices. */
export function updateLabVisual(draft: LabDraft, suggestion: LabSuggestion, regenerated = false): LabDraft {
  const old = draft.suggestions.find((entry) => entry.chunkId === suggestion.chunkId);
  const changed = regenerated || decisions(old) !== decisions(suggestion);
  const target = draft.chunks.findIndex((chunk) => chunk.id === suggestion.chunkId);
  const later = new Set(changed ? draft.chunks.slice(target + 1).map((chunk) => chunk.id) : []);
  return { ...draft,
    suggestions: [...draft.suggestions.filter((entry) => entry.chunkId !== suggestion.chunkId && !later.has(entry.chunkId)), suggestion],
    reviews: changed ? draft.reviews.filter((entry) => entry.chunkId !== suggestion.chunkId && !later.has(entry.chunkId)) : draft.reviews,
  };
}

export function updateLabIntent(draft: LabDraft, intent: LabIntentSuggestion, regenerated = false): LabDraft {
  const old = draft.intents.find((entry) => entry.chunkId === intent.chunkId);
  const changed = regenerated || decisions(old) !== decisions(intent);
  const target = draft.chunks.findIndex((chunk) => chunk.id === intent.chunkId);
  const affected = new Set(changed ? draft.chunks.slice(target).map((chunk) => chunk.id) : []);
  return { ...draft,
    intents: [...draft.intents.filter((entry) => entry.chunkId !== intent.chunkId), intent],
    suggestions: draft.suggestions.filter((entry) => !affected.has(entry.chunkId)),
    reviews: draft.reviews.filter((entry) => !affected.has(entry.chunkId)),
  };
}
